/**
 * Personalized activity generator — port of iOS
 * `client-native/.../generation/activityCoordinator.ts`, reconciled with the
 * PWA's existing `feed/activities/generateActivities.ts` data shapes.
 *
 * Pipeline (pure, testable):
 *   1. Filter to non-known, valid, unique candidate words.
 *   2. Score each by frequency tier (+ session-practice penalty + jitter) and
 *      sort — `scoreWordForSelection`. Super-Beginner (difficulty 1) skips
 *      frequency so all tiers are equal.
 *   3. `planOptimalAllocation` — the 13-case allocator that maximizes activity
 *      count across Match / MCQ / MCQ-reverse / Spelling given the available
 *      words and the REAL video level (threaded from `chapterInfo.videoLevel`).
 *   4. Fetch MCQ / reverse-MCQ / fill distractors in parallel (3s timeout race,
 *      random-word fallback) and build the unified `Activity[]`.
 *
 * Returns the unified activity list (mcq | mcq-reverse | match | write | fill).
 */
import type {
  GuessActivity,
  MatchActivity,
  Option,
  UserVideoWord,
  WriteActivity,
} from "@shared_types";
import { shuffleArray } from "@/lib/utils";
import { getWordFrequencyRank } from "@/data/spanishWordFrequency";
import type { Activity, FillData } from "../types";
import { sessionPracticeTracker } from "./sessionPracticeTracker";
import { fetchDistractors } from "./distractors";

/** Minimum word length for spelling activity. */
const SPELLING_MIN_WORD_LENGTH = 3;
/** Course difficulty that flags Super-Beginner — skip frequency scoring. */
const SUPER_BEGINNER_DIFFICULTY = 1;
/** Match needs 3-4 words; MCQ needs 4 total words for wrong answers. */
const MATCH_MIN_WORDS = 3;
const MCQ_MIN_TOTAL_WORDS = 4;

export interface ChapterInfo {
  /** Numeric video level (e.g. 1.28) — drives spelling gating. May be a CEFR-
   *  derived fallback when the real chapter level is absent, so it is NOT a
   *  reliable "real level" signal on its own (see hasRealVideoLevel). */
  videoLevel?: number;
  /** True only when `videoLevel` is the REAL chapter level (not a CEFR-derived
   *  fallback). iOS only fetches LLM distractors when a real level exists, so
   *  this gates that fetch — a synthetic fallback must not enable it. */
  hasRealVideoLevel?: boolean;
  /** Course difficulty (1 = Super-Beginner). */
  courseDifficulty?: number;
  /**
   * Source video's YouTube id. When provided, the generated Fill activity
   * carries it (+ the sentence's clip window) so the component can mount the
   * segment-looped video bubble above the sentence (iOS Fill.tsx:243-270).
   * Omitted → Fill renders the static sentence layout (no video bubble).
   */
  youtubeId?: string;
  /**
   * Cloudflare Stream id + preferred player threaded into the Fill video bubble
   * so Cloudflare-hosted lessons play the chat-reveal clip too (iOS Fill mounts
   * VideoPlayer with `playerType` + `cloudflareId`). Either a youtubeId OR a
   * cloudflareId is enough to render the video bubble.
   */
  cloudflareId?: string;
  player?: "unset" | "cloudflare" | "youtube";
  /** User's first name for the personalized Fill prompt bubble. */
  firstName?: string;
}

/**
 * The activity types this generator can emit (iOS `getAvailableActivities`
 * keys). The host threads the user's per-type toggles in as `enabledTypes`;
 * only enabled types are produced AND selected.
 */
export type GeneratableActivityType =
  | "mcq"
  | "mcq-reverse"
  | "match"
  | "write"
  | "fill";

const ALL_GENERATABLE_TYPES: readonly GeneratableActivityType[] = [
  "mcq",
  "mcq-reverse",
  "match",
  "write",
  "fill",
];

export interface GenerateOptions {
  /** Cap on returned activities (iOS default 3; Super-Beginner 4). */
  maxActivities?: number;
  /**
   * Activity types the user has enabled (iOS `getAvailableActivities`). When
   * omitted, all types are eligible (back-compat). When provided, only these
   * types are generated and selected — mirrors iOS per-type setting gating
   * (`isGuessTheWordEnabled` → mcq + mcq-reverse, `isMatchTheWordsEnabled` →
   * match, `isSpellTheWordEnabled` → write, `isFillInTheBlankEnabled` → fill).
   */
  enabledTypes?: readonly GeneratableActivityType[];
  /**
   * Lesson id for the per-lesson random-selection cache (iOS
   * `getRandomActivitySelection`, MMKV key `lesson_${id}_activities_${count}`
   * — localStorage here). Omitted → fresh random selection per call.
   */
  lessonId?: number;
}

// ============================================================================
// Word validity (mirrors generators.ts + the existing feed generator)
// ============================================================================

/** Drop punctuation/number-only words and words whose target == native. */
function isUsableWord(w: UserVideoWord): boolean {
  const target = w.definition?.targetWord?.trim();
  const native = w.definition?.nativeWord?.trim();
  if (!target || !native) return false;
  if (target.toLowerCase() === native.toLowerCase()) return false;
  if (/^[\d\p{P}\s]+$/u.test(target)) return false;
  return true;
}

// ============================================================================
// Frequency-based word scoring (activityCoordinator.ts:47-90)
// ============================================================================

export function scoreWordForSelection(
  word: UserVideoWord,
  isSuperBeginner: boolean,
): number {
  let score = 0;

  if (!isSuperBeginner) {
    const rank = getWordFrequencyRank(word.wordKey);
    if (rank === undefined) score += 0; // rare/name — low priority
    else if (rank <= 99) score += 1; // super-common — boring
    else if (rank <= 999) score += 2; // very-common — medium
    else if (rank <= 7000) score += 3; // best practice band
    else score += 0; // rare (7001+)
  }

  score -= sessionPracticeTracker.getPracticeCount(word.wordKey);
  score += Math.random() * 0.9; // jitter for within-tier variety
  return score;
}

function sortBySelectionScore(
  words: UserVideoWord[],
  courseDifficulty?: number,
): UserVideoWord[] {
  const isSuperBeginner = courseDifficulty === SUPER_BEGINNER_DIFFICULTY;
  // Score each word ONCE (the score includes a `Math.random()` jitter, so it
  // must not be recomputed inside the comparator — an inconsistent/
  // non-transitive comparator makes `Array.sort` undefined behavior). Roll the
  // jitter once per word here, then sort by the precomputed (stable) score.
  return words
    .map((word) => ({ word, score: scoreWordForSelection(word, isSuperBeginner) }))
    .sort((a, b) => b.score - a.score)
    .map(({ word }) => word);
}

// ============================================================================
// Spelling level gating (activityCoordinator.ts:119-137)
// ============================================================================

export function getMaxWordLengthForLevel(
  videoLevel: number | undefined,
): number | null {
  if (!videoLevel || videoLevel < 1.0) return null; // A0 — no spelling
  if (videoLevel < 1.5) return 5; // lower A1
  if (videoLevel < 2.5) return 7; // upper A1 / lower A2
  return 11; // B1+
}

// ============================================================================
// Optimal allocation planner (activityCoordinator.ts:155-370) — 13 cases
// ============================================================================

export interface ActivityAllocationPlan {
  matchWords: number; // 0, 3, or 4
  mcqWords: number; // 0 or 1
  mcqReverseWords: number; // 0 or 1
  spellingWords: number; // 0 or 1
  totalActivities: number;
}

export function planOptimalAllocation(
  totalLearningWords: number,
  totalValidWords: number,
  spellingEligibleWords: number,
  videoLevel: number | undefined,
): ActivityAllocationPlan {
  const maxSpellingLength = getMaxWordLengthForLevel(videoLevel);
  const canDoSpelling = maxSpellingLength !== null && spellingEligibleWords >= 1;
  const canDoMCQ =
    totalLearningWords >= 1 && totalValidWords >= MCQ_MIN_TOTAL_WORDS;
  const canDoMatch = totalLearningWords >= MATCH_MIN_WORDS;

  // Case 1: ALL (Match + MCQ + Reverse + Spelling)
  if (canDoMatch && canDoMCQ && canDoSpelling && totalLearningWords >= 6) {
    return { matchWords: 3, mcqWords: 1, mcqReverseWords: 1, spellingWords: 1, totalActivities: 4 };
  }
  // Case 2: Match + both MCQs (no spelling)
  if (canDoMatch && canDoMCQ && totalLearningWords >= 5 && !canDoSpelling) {
    return { matchWords: 3, mcqWords: 1, mcqReverseWords: 1, spellingWords: 0, totalActivities: 3 };
  }
  // Case 3: Match + one MCQ + Spelling
  if (canDoMatch && canDoMCQ && canDoSpelling && totalLearningWords === 5) {
    return { matchWords: 3, mcqWords: 1, mcqReverseWords: 0, spellingWords: 1, totalActivities: 3 };
  }
  // Case 4: 2 activities with 4 words
  if (totalLearningWords === 4) {
    if (canDoMatch && canDoMCQ) {
      return { matchWords: 3, mcqWords: 1, mcqReverseWords: 0, spellingWords: 0, totalActivities: 2 };
    }
    if (canDoMatch && canDoSpelling) {
      return { matchWords: 3, mcqWords: 0, mcqReverseWords: 0, spellingWords: 1, totalActivities: 2 };
    }
  }
  // Case 5: 2 activities with 3 words
  if (totalLearningWords === 3) {
    if (canDoMCQ && canDoSpelling) {
      return { matchWords: 0, mcqWords: 1, mcqReverseWords: 0, spellingWords: 1, totalActivities: 2 };
    }
    if (canDoMatch) {
      return { matchWords: 3, mcqWords: 0, mcqReverseWords: 0, spellingWords: 0, totalActivities: 1 };
    }
  }
  // Case 6: 1-2 activities with 2 words
  if (totalLearningWords === 2) {
    if (canDoMCQ && canDoSpelling) {
      return { matchWords: 0, mcqWords: 1, mcqReverseWords: 0, spellingWords: 1, totalActivities: 2 };
    }
    if (canDoMCQ) {
      return { matchWords: 0, mcqWords: 1, mcqReverseWords: 1, spellingWords: 0, totalActivities: 2 };
    }
    if (canDoSpelling) {
      return { matchWords: 0, mcqWords: 0, mcqReverseWords: 0, spellingWords: 1, totalActivities: 1 };
    }
  }
  // Case 7: 1 word
  if (totalLearningWords === 1) {
    if (canDoMCQ) {
      return { matchWords: 0, mcqWords: 1, mcqReverseWords: 0, spellingWords: 0, totalActivities: 1 };
    }
    if (canDoSpelling) {
      return { matchWords: 0, mcqWords: 0, mcqReverseWords: 0, spellingWords: 1, totalActivities: 1 };
    }
  }
  // Case 8: fallback — Match + both MCQs
  if (canDoMatch && canDoMCQ && totalLearningWords >= 5) {
    return { matchWords: 3, mcqWords: 1, mcqReverseWords: 1, spellingWords: 0, totalActivities: 3 };
  }
  // Case 9: fallback — Match + one MCQ
  if (canDoMatch && canDoMCQ) {
    return {
      matchWords: Math.min(4, totalLearningWords - 1),
      mcqWords: 1,
      mcqReverseWords: 0,
      spellingWords: 0,
      totalActivities: 2,
    };
  }
  // Case 10: fallback — just Match
  if (canDoMatch) {
    return {
      matchWords: Math.min(4, totalLearningWords),
      mcqWords: 0,
      mcqReverseWords: 0,
      spellingWords: 0,
      totalActivities: 1,
    };
  }
  // Case 11: fallback — both MCQs
  if (canDoMCQ && totalLearningWords >= 2) {
    return { matchWords: 0, mcqWords: 1, mcqReverseWords: 1, spellingWords: 0, totalActivities: 2 };
  }
  // Case 12: fallback — one MCQ
  if (canDoMCQ) {
    return { matchWords: 0, mcqWords: 1, mcqReverseWords: 0, spellingWords: 0, totalActivities: 1 };
  }
  // Case 13: none
  return { matchWords: 0, mcqWords: 0, mcqReverseWords: 0, spellingWords: 0, totalActivities: 0 };
}

// ============================================================================
// Activity builders (activityCoordinator.ts:488-579)
// ============================================================================

/** Fill `wrong` up to `count` from a pool, skipping the correct answer + dupes. */
function topUp(
  wrong: string[],
  correctLc: string,
  pool: string[],
  count: number,
): string[] {
  for (const candidate of shuffleArray(pool)) {
    if (wrong.length >= count) break;
    const c = candidate.toLowerCase();
    if (c === correctLc) continue;
    if (wrong.some((x) => x.toLowerCase() === c)) continue;
    wrong.push(candidate);
  }
  return wrong;
}

/** Forward MCQ: question = Spanish, options = English. */
function buildMCQ(
  questionWord: UserVideoWord,
  distractors: string[],
  others: UserVideoWord[],
): GuessActivity {
  const correct = questionWord.definition.nativeWord;
  const correctLc = correct.toLowerCase();
  const wrong = topUp(
    distractors.filter((d) => d.toLowerCase() !== correctLc).slice(0, 3),
    correctLc,
    others.map((w) => w.definition.nativeWord),
    3,
  );
  const options: Option[] = shuffleArray([
    { choice: correct, correct: true },
    ...wrong.slice(0, 3).map((choice) => ({ choice, correct: false })),
  ]);
  return { question: questionWord.definition.targetWord, options, approved: true };
}

/** Reverse MCQ: question = English, options = Spanish (distractorLanguage 'spanish'). */
function buildReverseMCQ(
  questionWord: UserVideoWord,
  distractors: string[],
  others: UserVideoWord[],
): GuessActivity {
  const correct = questionWord.definition.targetWord;
  const correctLc = correct.toLowerCase();
  const wrong = topUp(
    distractors.filter((d) => d.toLowerCase() !== correctLc).slice(0, 3),
    correctLc,
    others.map((w) => w.definition.targetWord),
    3,
  );
  const options: Option[] = shuffleArray([
    { choice: correct, correct: true },
    ...wrong.slice(0, 3).map((choice) => ({ choice, correct: false })),
  ]);
  return { question: questionWord.definition.nativeWord, options, approved: true };
}

function buildMatch(words: UserVideoWord[]): MatchActivity {
  const matchData = shuffleArray(
    words.map((w) => ({
      spanish: w.definition.targetWord,
      english: w.definition.nativeWord,
    })),
  );
  return { matchData, approved: true };
}

function buildWrite(word: UserVideoWord): WriteActivity {
  return {
    question: word.definition.nativeWord,
    answer: word.definition.targetWord,
    approved: true,
  };
}

interface FillSelection {
  sentenceWords: UserVideoWord[];
  blank: UserVideoWord;
}

/** Reconstruct sentences from the ordered transcript words (split on . ! ?). */
function selectFillSentence(
  words: UserVideoWord[],
  usedKeys: Set<string>,
): FillSelection | null {
  const sentences: UserVideoWord[][] = [];
  let current: UserVideoWord[] = [];
  for (const w of words) {
    current.push(w);
    const last = w.displayText.trim().slice(-1);
    if (last === "." || last === "!" || last === "?") {
      sentences.push(current);
      current = [];
    }
  }
  if (current.length) sentences.push(current);

  // A blank-able word is any non-known word (iOS fillGenerator: status !==
  // "known" && !== "deleted"). Lesson words start as "new", so gating on
  // status === "learning" (the old web predicate) meant a user who only watched
  // lessons — words still "new" — never got a Fill activity. Match iOS: new +
  // learning are both valid.
  const isBlankable = (w: { status?: string }) =>
    w.status !== "known" && w.status !== "deleted";

  const candidates = shuffleArray(
    sentences.filter(
      (s) =>
        s.length >= 4 &&
        s.length <= 12 &&
        s.some((w) => isBlankable(w)),
    ),
  );

  for (const sentence of candidates) {
    const middle = sentence.filter(
      (w, i) =>
        i !== 0 &&
        i !== sentence.length - 1 &&
        isBlankable(w) &&
        isUsableWord(w) &&
        (w.definition.targetWord?.length ?? 0) >= 3,
    );
    const learning = sentence.filter(
      (w) => isBlankable(w) && isUsableWord(w),
    );
    const pool = (middle.length ? middle : learning).filter(
      (w) => !usedKeys.has(w.wordKey),
    );
    const finalPool = pool.length ? pool : middle.length ? middle : learning;
    if (finalPool.length) {
      return { sentenceWords: sentence, blank: shuffleArray(finalPool)[0] };
    }
  }
  return null;
}

/** Clip window (ms) spanning the selected sentence's words (iOS uses the
 * sentence's start/end). Words carry `start` + `duration` (ms). */
function fillClipWindow(
  sentenceWords: UserVideoWord[],
): { start: number; end: number } {
  const starts = sentenceWords.map((w) => w.start);
  const ends = sentenceWords.map((w) => w.start + (w.duration || 0));
  return {
    start: Math.min(...starts),
    end: Math.max(...ends),
  };
}

function buildFill(
  selection: FillSelection,
  distractors: string[],
  candidatePool: UserVideoWord[],
  video: {
    youtubeId?: string;
    cloudflareId?: string;
    player?: "unset" | "cloudflare" | "youtube";
    firstName?: string;
  },
): FillData {
  const correct = selection.blank.definition.targetWord;
  const correctLc = correct.toLowerCase();
  const wrong = topUp(
    distractors.filter((d) => d.toLowerCase() !== correctLc).slice(0, 2),
    correctLc,
    candidatePool
      .filter((w) => w.wordKey !== selection.blank.wordKey)
      .map((w) => w.definition.targetWord),
    2,
  );
  // Preserve leading/trailing punctuation on the blanked word so
  // "¡Hola!" → "¡_____!" rather than a bare "_____"
  // (iOS fillGenerator.ts:260-278 createQuestionWithBlank).
  const question = selection.sentenceWords
    .map((w) => {
      if (w.videoWordId !== selection.blank.videoWordId) return w.displayText;
      const text = w.displayText;
      const leadingPunc = text.match(/^[.,!?;:"""''()¿¡\-—]+/)?.[0] ?? "";
      const trailingPunc = text.match(/[.,!?;:"""''()¿¡\-—]+$/)?.[0] ?? "";
      return leadingPunc + "_____" + trailingPunc;
    })
    .join(" ");
  const options: Option[] = shuffleArray([
    { choice: correct, correct: true },
    ...wrong.slice(0, 2).map((choice) => ({ choice, correct: false })),
  ]);
  const { youtubeId, cloudflareId, player, firstName } = video;
  if (youtubeId || cloudflareId) {
    const { start, end } = fillClipWindow(selection.sentenceWords);
    return {
      question,
      options,
      youtubeId,
      cloudflareId,
      player,
      start,
      end,
      firstName,
    };
  }
  return { question, options, firstName };
}

// ============================================================================
// Entry point
// ============================================================================

/**
 * Generate the unified personalized activity list for a lesson's words.
 *
 * @param words — all lesson words (with up-to-date status).
 * @param chapterInfo — REAL video level + course difficulty (threads spelling
 *   gating and Super-Beginner frequency-neutral scoring). Pass the chapter's
 *   real numeric `videoLevel` (iOS `useChapterInfo`) and `courseDifficulty`
 *   (iOS `course.level.difficulty`) — NOT values derived from CEFR placement.
 * @param opts — optional cap + the user's enabled activity types
 *   (`enabledTypes`, iOS `getAvailableActivities`) + `lessonId` for the
 *   per-lesson random-selection cache. Only enabled types are generated/
 *   selected. Cap defaults to the allocation plan / Super-Beginner 4.
 * @returns up to `maxActivities` activities — random primary selection with
 *   priority-ordered backfill (iOS `selectFinalActivities`) — or `null`.
 */
export async function generateActivitiesForLesson(
  words: UserVideoWord[],
  chapterInfo: ChapterInfo = {},
  opts: GenerateOptions = {},
): Promise<Activity[] | null> {
  const {
    videoLevel,
    hasRealVideoLevel,
    courseDifficulty,
    youtubeId,
    cloudflareId,
    player,
    firstName,
  } = chapterInfo;

  // Per-type user-settings gating (iOS getAvailableActivities). Omitted →
  // all types eligible (back-compat for callers not yet threading settings).
  const enabled = new Set<GeneratableActivityType>(
    opts.enabledTypes ?? ALL_GENERATABLE_TYPES,
  );

  // 1. candidates: non-known, valid, unique
  const seen = new Set<string>();
  const candidates = words.filter((w) => {
    if (w.status === "known" || w.status === "deleted") return false;
    if (!isUsableWord(w)) return false;
    if (seen.has(w.wordKey)) return false;
    seen.add(w.wordKey);
    return true;
  });
  if (candidates.length === 0) return null;

  // all valid words (incl. known) usable as MCQ wrong-answers
  const validSeen = new Set<string>();
  const allValidWords = words.filter((w) => {
    if (!isUsableWord(w)) return false;
    if (validSeen.has(w.wordKey)) return false;
    validSeen.add(w.wordKey);
    return true;
  });

  // 2. score + sort
  const sorted = sortBySelectionScore(candidates, courseDifficulty);

  // count spelling-eligible words for the planner
  const maxWordLength = getMaxWordLengthForLevel(videoLevel);
  const spellingEligibleWords =
    maxWordLength === null
      ? 0
      : sorted.filter((w) => {
          const len = w.definition.targetWord.length;
          return len >= SPELLING_MIN_WORD_LENGTH && len <= maxWordLength;
        }).length;

  // 3. plan, then zero out any disabled types so we never select/build words
  //    for an activity the user has turned off (iOS gates generation on
  //    getAvailableActivities; here we suppress the allocation slots instead).
  const rawPlan = planOptimalAllocation(
    sorted.length,
    allValidWords.length,
    spellingEligibleWords,
    videoLevel,
  );
  const plan: ActivityAllocationPlan = {
    matchWords: enabled.has("match") ? rawPlan.matchWords : 0,
    mcqWords: enabled.has("mcq") ? rawPlan.mcqWords : 0,
    mcqReverseWords: enabled.has("mcq-reverse") ? rawPlan.mcqReverseWords : 0,
    spellingWords: enabled.has("write") ? rawPlan.spellingWords : 0,
    totalActivities: rawPlan.totalActivities,
  };

  const used = new Set<string>();
  const practicedKeys: string[] = [];
  let idx = 0;

  // --- select Match words ---
  let matchWords: UserVideoWord[] = [];
  if (plan.matchWords > 0) {
    matchWords = sorted.slice(idx, idx + plan.matchWords);
    matchWords.forEach((w) => {
      used.add(w.wordKey);
      practicedKeys.push(w.wordKey);
    });
    idx += plan.matchWords;
  }

  // --- select MCQ word ---
  let mcqWord: UserVideoWord | null = null;
  let mcqOthers: UserVideoWord[] = [];
  if (plan.mcqWords > 0 && idx < sorted.length) {
    const w = sorted[idx];
    const others = allValidWords.filter(
      (o) => o.wordKey !== w.wordKey && !used.has(o.wordKey),
    );
    if (others.length >= 3) {
      mcqWord = w;
      mcqOthers = others;
      used.add(w.wordKey);
      practicedKeys.push(w.wordKey);
    }
    idx += 1;
  }

  // --- select reverse MCQ word ---
  let reverseWord: UserVideoWord | null = null;
  let reverseOthers: UserVideoWord[] = [];
  if (plan.mcqReverseWords > 0 && idx < sorted.length) {
    const w = sorted[idx];
    const others = allValidWords.filter(
      (o) => o.wordKey !== w.wordKey && !used.has(o.wordKey),
    );
    if (others.length >= 3) {
      reverseWord = w;
      reverseOthers = others;
      used.add(w.wordKey);
      practicedKeys.push(w.wordKey);
    }
    idx += 1;
  }

  // --- select Spelling word ---
  let spellingWord: UserVideoWord | null = null;
  if (plan.spellingWords > 0 && maxWordLength !== null) {
    for (let i = idx; i < sorted.length; i++) {
      const len = sorted[i].definition.targetWord.length;
      if (len >= SPELLING_MIN_WORD_LENGTH && len <= maxWordLength) {
        spellingWord = sorted[i];
        used.add(spellingWord.wordKey);
        practicedKeys.push(spellingWord.wordKey);
        break;
      }
    }
  }

  // --- select Fill sentence (uses full ordered word list) ---
  const fillSel = enabled.has("fill") ? selectFillSentence(words, used) : null;
  if (fillSel) {
    used.add(fillSel.blank.wordKey);
    practicedKeys.push(fillSel.blank.wordKey);
  }

  // 4. fetch distractors in parallel (3s timeout race inside fetchDistractors).
  //    iOS only enqueues the LLM distractor fetch when a REAL positive video
  //    level exists (activityCoordinator.ts:776,784,791); without one it skips
  //    the call and the builders fall back to random pool words. We do the same
  //    rather than inventing a synthetic request for a level-less video — so a
  //    CEFR-derived spelling fallback (videoLevel set, hasRealVideoLevel false)
  //    must NOT enable it. `realVideoLevel` narrows to `number` in the truthy
  //    branch, so the fetchDistractors calls need no cast.
  const realVideoLevel =
    hasRealVideoLevel && videoLevel && videoLevel > 0 ? videoLevel : undefined;
  const [mcqDistractors, reverseDistractors, fillDistractors] =
    await Promise.all([
      mcqWord && realVideoLevel !== undefined
        ? fetchDistractors({
            wordKey: mcqWord.wordKey,
            correctNativeWord: mcqWord.definition.nativeWord,
            availableWords: mcqOthers.map((w) => w.definition.nativeWord),
            videoLevel: realVideoLevel,
            distractorLanguage: "english",
          })
        : Promise.resolve<string[]>([]),
      reverseWord && realVideoLevel !== undefined
        ? fetchDistractors({
            wordKey: reverseWord.wordKey,
            correctNativeWord: reverseWord.definition.nativeWord,
            availableWords: reverseOthers.map((w) => w.definition.targetWord),
            videoLevel: realVideoLevel,
            distractorLanguage: "spanish",
          })
        : Promise.resolve<string[]>([]),
      fillSel && realVideoLevel !== undefined
        ? fetchDistractors({
            wordKey: fillSel.blank.wordKey,
            correctNativeWord: fillSel.blank.definition.nativeWord,
            availableWords: candidates
              .filter((w) => w.wordKey !== fillSel.blank.wordKey)
              .map((w) => w.definition.targetWord),
            videoLevel: realVideoLevel,
            distractorLanguage: "spanish",
          })
        : Promise.resolve<string[]>([]),
    ]);

  // build
  const activities: Activity[] = [];
  if (mcqWord) {
    activities.push({ type: "mcq", data: buildMCQ(mcqWord, mcqDistractors, mcqOthers) });
  }
  if (reverseWord) {
    activities.push({
      type: "mcq-reverse",
      data: buildReverseMCQ(reverseWord, reverseDistractors, reverseOthers),
    });
  }
  if (matchWords.length >= MATCH_MIN_WORDS) {
    activities.push({ type: "match", data: buildMatch(matchWords) });
  }
  if (spellingWord) {
    activities.push({ type: "write", data: buildWrite(spellingWord) });
  }
  if (fillSel) {
    activities.push({
      type: "fill",
      data: buildFill(fillSel, fillDistractors, candidates, {
        youtubeId,
        cloudflareId,
        player,
        firstName,
      }),
    });
  }

  // record practiced words for future deprioritization this session
  if (practicedKeys.length) sessionPracticeTracker.recordPracticed(practicedKeys);

  const cap =
    opts.maxActivities ??
    (courseDifficulty === SUPER_BEGINNER_DIFFICULTY ? 4 : 3);
  const randomSelection = getRandomActivitySelection(
    opts.lessonId,
    ALL_GENERATABLE_TYPES.filter((t) => enabled.has(t)),
    cap,
  );
  const final = selectFinalActivities(randomSelection, activities, cap);
  return final.length ? final : null;
}

/**
 * Random primary selection of activity TYPES for this lesson (iOS
 * `getRandomActivitySelection`, activitySelection.ts:62-98): shuffle the
 * user-enabled types, slice to `count`, cache per lesson so re-entering the
 * same lesson shows the same activity mix (iOS persists in MMKV; localStorage
 * here). A cached selection is reused only if every entry is still enabled and
 * the count matches. Note the pool excludes `sentence` — on web the backend
 * Sentence activity is a separate KEEP-WEB route, not a practice-block slot.
 */
function getRandomActivitySelection(
  lessonId: number | undefined,
  availableActivities: GeneratableActivityType[],
  count: number,
): GeneratableActivityType[] {
  const storageKey =
    lessonId && lessonId > 0 ? `lesson_${lessonId}_activities_${count}` : null;

  if (storageKey) {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as GeneratableActivityType[];
        const areStoredActivitiesValid =
          Array.isArray(parsed) &&
          parsed.every((a) => availableActivities.includes(a));
        if (areStoredActivitiesValid && parsed.length === count) {
          return parsed;
        }
      }
    } catch {
      // corrupt cache — fall through and regenerate
    }
  }

  const selected = shuffleArray(availableActivities).slice(0, count);
  if (storageKey) {
    try {
      localStorage.setItem(storageKey, JSON.stringify(selected));
    } catch {
      // private mode / quota — selection just won't be sticky
    }
  }
  return selected;
}

/**
 * iOS `selectFinalActivities` (activitySelection.ts:130-213): the random
 * selection is PRIMARY — include each randomly selected type that actually
 * generated — and the 1-word-first priority order (mcq, mcq-reverse, write,
 * fill, then match) is only FALLBACK backfill up to `targetCount`. Final order
 * is shuffled for variety.
 */
function selectFinalActivities(
  randomSelection: GeneratableActivityType[],
  activities: Activity[],
  targetCount: number,
): Activity[] {
  const byType = new Map(activities.map((a) => [a.type, a] as const));
  const included: Activity[] = [];

  randomSelection.forEach((type) => {
    const generated = byType.get(type);
    if (generated) included.push(generated);
  });

  if (included.length < targetCount) {
    const BACKUP_PRIORITY: readonly GeneratableActivityType[] = [
      "mcq",
      "mcq-reverse",
      "write",
      "fill",
      "match",
    ];
    const backups = BACKUP_PRIORITY.filter(
      (t) => byType.has(t) && !included.some((a) => a.type === t),
    ).map((t) => byType.get(t)!);
    included.push(...backups.slice(0, targetCount - included.length));
  }

  return shuffleArray(included);
}
